import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Stack, Group, Button, SegmentedControl, Switch, Title, Paper, Text, Alert, Loader, Center,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { api, imageUrl } from '../../api/client';
import { presetDesign, type PdfDesign, type DetailsStyle, type TimingStyle } from './pdfDesign';
import { generateQuotationPdf, type CompanyInfo, type CustomerInfo, type OrderInfo, type ResolvedRef } from './pdfGenerator';
import type { QuotationDetail } from './NewQuotationModal';
import type { OrderPart } from './OrderPartModal';
import { IconClipboard, IconBlueprint, IconQuote } from '../../components/icons/CncIcons';

interface Props {
  orderId: number;
  quotation: QuotationDetail;
  parts: OrderPart[];
  onBack: () => void;
  onExported: () => void;
}

export default function CreatePdfView({ orderId, quotation, parts, onBack, onExported }: Props) {
  // Seed design from the quotation's persisted pdf_design (if any), else a Transparent preset.
  const initial = useMemo<PdfDesign>(() => {
    const saved: any = (quotation as any).pdfDesign;
    if (saved && saved.style && saved.basics && saved.equipment) return saved as PdfDesign;
    return presetDesign('Transparent');
  }, [quotation]);

  const [design, setDesign] = useState<PdfDesign>(initial);
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [customer, setCustomer] = useState<CustomerInfo | null>(null);
  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [refs, setRefs] = useState<ResolvedRef | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const lastBlobRef = useRef<Blob | null>(null);

  // Load all reference data once.
  useEffect(() => {
    (async () => {
      try {
        const [adm, orderDetail] = await Promise.all([
          api.get<any>('/admin/settings').catch(() => null),
          api.get<any>(`/orders/${orderId}`),
        ]);
        setCompany({
          logoPath:       adm?.companyLogoPath ?? null,
          name:           adm?.companyName ?? null,
          legalName:      adm?.companyLegalName ?? null,
          addressCountry: adm?.companyAddressCountry ?? null,
          addressCounty:  adm?.companyAddressCounty ?? null,
          addressStreet:  adm?.companyAddressStreet ?? null,
          addressZip:     adm?.companyAddressZip ?? null,
        });
        setOrder({
          orderUid: orderDetail.orderUid,
          customerName: orderDetail.customerName,
          customerLogo: orderDetail.customerLogo,
          contactName: orderDetail.contactName,
          technicianName: orderDetail.technicianName ?? quotation.technicianName ?? null,
        });
        if (orderDetail.customerId) {
          const c = await api.get<any>(`/customers/${orderDetail.customerId}`);
          setCustomer({
            name: c.name,
            legalName: c.legalName,
            address: c.address,
            legal: { companyName: c.legal?.companyName ?? null, vatNumber: c.legal?.vatNumber ?? null },
          });
        }
        // Collect ref ids used across processes, resolve in parallel.
        const equipmentIds = new Set<number>();
        const technicianIds = new Set<number>();
        const consumableIds = new Set<number>();
        for (const qp of quotation.parts) {
          for (const pr of qp.processes) {
            if (pr.equipmentId) equipmentIds.add(pr.equipmentId);
            if (pr.technicianId) technicianIds.add(pr.technicianId);
            if (pr.consumableId) consumableIds.add(pr.consumableId);
            for (const c of pr.consumables) if (c.consumableId) consumableIds.add(c.consumableId);
          }
        }
        const equipmentById = new Map<number, any>();
        const consumableById = new Map<number, any>();
        const accessoryById = new Map<number, any>();
        const technicianById = new Map<number, any>();
        await Promise.all([
          ...Array.from(equipmentIds).map(async (id) => {
            try {
              const e = await api.get<any>(`/equipment/${id}`);
              equipmentById.set(id, e);
              for (const a of e.accessories ?? []) accessoryById.set(a.id, a);
            } catch { /* ignore */ }
          }),
          ...Array.from(consumableIds).map(async (id) => {
            try { consumableById.set(id, await api.get<any>(`/consumables/${id}`)); } catch { /* ignore */ }
          }),
          ...Array.from(technicianIds).map(async (id) => {
            try { technicianById.set(id, await api.get<any>(`/technicians/${id}`)); } catch { /* ignore */ }
          }),
        ]);
        setRefs({ equipmentById, consumableById, accessoryById, technicianById });
      } catch (e: any) {
        setErr(e.message ?? 'Failed to load PDF data');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Regenerate preview whenever the design or the loaded refs change.
  useEffect(() => {
    if (!company || !order || !refs) return;
    let cancelled = false;
    setRendering(true);
    (async () => {
      try {
        const blob = await generateQuotationPdf({
          design, quotation, orderParts: parts, order, customer, company, refs,
          imageUrlFor: (p) => imageUrl(p),
        });
        if (cancelled) return;
        lastBlobRef.current = blob;
        const url = URL.createObjectURL(blob);
        setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      } catch (e: any) {
        setErr(e.message ?? 'PDF render failed');
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();
    return () => { cancelled = true; };
  }, [design, company, customer, order, refs, quotation, parts]);

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function exportPdf() {
    if (!lastBlobRef.current) return;
    setBusy(true);
    setErr(null);
    try {
      const blob = lastBlobRef.current;
      const buf = await blob.arrayBuffer();
      // 1) Upload to server so it is archived in data/quotations/<UID>.pdf.
      const r = await fetch(
        `/api/v1/orders/${orderId}/quotations/${quotation.id}/pdf?design=${encodeURIComponent(JSON.stringify(design))}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/pdf' },
          body: buf,
        }
      );
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      // 2) Also trigger a local download for the user so they end up with a copy on their machine.
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${quotation.quotationUid}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a short delay so Safari has time to start the download.
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 2000);
      notifications.show({
        message: 'Quotation exported. PDF archived on the server and downloaded to your device.',
        color: 'green',
      });
      onExported();
    } catch (e: any) {
      setErr(e.message ?? 'Export failed');
    } finally { setBusy(false); }
  }

  function onStyleChange(v: string) {
    const s = v as DetailsStyle;
    setDesign(presetDesign(s));
  }

  function patch<K extends keyof PdfDesign>(section: K, partial: Partial<PdfDesign[K]>) {
    setDesign((d) => ({ ...d, [section]: { ...(d[section] as any), ...partial } as PdfDesign[K] }));
  }

  if (!company || !order || !refs) return <Center p="xl"><Loader /></Center>;

  return (
    <Stack>
      <Group justify="space-between">
        <Group gap={10}><IconQuote size={20} /><Title order={4}>Create Quotation PDF</Title></Group>
        <Button variant="default" data-tour="pdf-back" onClick={onBack}>← Back to quotation</Button>
      </Group>

      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 16 }}>
        {/* Design panel */}
        <Paper withBorder p="sm">
          <Stack gap="sm">
            <Group gap={6}><IconBlueprint size={14} /><Text fw={600}>Design Quotation</Text></Group>

            <Stack gap={2}>
              <Text size="xs" c="dimmed">Order Details Style</Text>
              <SegmentedControl
                fullWidth
                data-tour="pdf-style"
                value={design.style}
                onChange={onStyleChange}
                data={[
                  { value: 'Closed Book', label: 'Closed' },
                  { value: 'Transparent', label: 'Transparent' },
                  { value: 'Open Book',   label: 'Open' },
                ]}
              />
            </Stack>

            <Text size="xs" fw={600} c="dimmed" mt="xs">Basics</Text>
            <Switch label="Include company logo" checked={design.basics.includeCompanyLogo} onChange={(e) => patch('basics', { includeCompanyLogo: e.currentTarget.checked })} />
            <Switch label="Include customer logo" checked={design.basics.includeCustomerLogo} onChange={(e) => patch('basics', { includeCustomerLogo: e.currentTarget.checked })} />
            <Switch label="Include parts icon" checked={design.basics.includePartsIcon} onChange={(e) => patch('basics', { includePartsIcon: e.currentTarget.checked })} />
            <Switch label="Include processes per part" checked={design.basics.includeProcessesPerPart} onChange={(e) => patch('basics', { includeProcessesPerPart: e.currentTarget.checked })} />

            <Stack gap={2}>
              <Text size="xs" c="dimmed">Details for Timing</Text>
              <SegmentedControl
                fullWidth
                value={design.basics.timingStyle}
                onChange={(v) => patch('basics', { timingStyle: v as TimingStyle })}
                data={[
                  { value: 'Closed Book', label: 'Closed' },
                  { value: 'Transparent', label: 'Transparent' },
                  { value: 'Open Book',   label: 'Open' },
                ]}
              />
            </Stack>

            <Switch label="Include gantt" checked={design.basics.includeGantt} onChange={(e) => patch('basics', { includeGantt: e.currentTarget.checked })} />

            <Text size="xs" fw={600} c="dimmed" mt="xs">Equipment</Text>
            <Switch label="Include equipment icons" checked={design.equipment.includeEquipmentIcons} onChange={(e) => patch('equipment', { includeEquipmentIcons: e.currentTarget.checked })} />
            <Switch label="Include accessories" checked={design.equipment.includeAccessories} onChange={(e) => patch('equipment', { includeAccessories: e.currentTarget.checked })} />
            <Switch label="Include equipment consumables" checked={design.equipment.includeEquipmentConsumables} onChange={(e) => patch('equipment', { includeEquipmentConsumables: e.currentTarget.checked })} />

            <Text size="xs" fw={600} c="dimmed" mt="xs">Lab technicians</Text>
            <Switch label="Include names" checked={design.technicians.includeNames} onChange={(e) => patch('technicians', { includeNames: e.currentTarget.checked })} />

            <Text size="xs" fw={600} c="dimmed" mt="xs">Consumables</Text>
            <Switch label="Include consumables" checked={design.consumables.includeConsumables} onChange={(e) => patch('consumables', { includeConsumables: e.currentTarget.checked })} />

            <Text size="xs" fw={600} c="dimmed" mt="xs">Markups</Text>
            <Switch label="Include markups" checked={design.markups.includeMarkups} onChange={(e) => patch('markups', { includeMarkups: e.currentTarget.checked })} />
            <Switch label="Include markups for each" checked={design.markups.includeMarkupsForEach} onChange={(e) => patch('markups', { includeMarkupsForEach: e.currentTarget.checked })} />
          </Stack>
        </Paper>

        {/* Preview */}
        <Paper withBorder p={0} style={{ minHeight: 560, position: 'relative' }}>
          {rendering && (
            <div style={{ position: 'absolute', top: 8, right: 12, zIndex: 2 }}>
              <Group gap={4}><Loader size="xs" /><Text size="xs" c="dimmed">Rendering…</Text></Group>
            </div>
          )}
          {previewUrl ? (
            <iframe
              title="PDF preview"
              src={previewUrl}
              style={{ width: '100%', height: '70vh', border: 0, display: 'block' }}
            />
          ) : (
            <Center p="xl"><Loader /></Center>
          )}
        </Paper>
      </div>

      {err && <Alert color="red">{err}</Alert>}
      <Group justify="flex-end" mt="sm">
        <Group gap={6}>
          <IconClipboard size={14} />
          <Text size="xs" c="dimmed">Exporting will mark the quotation as <b>EXPORTED</b> and make it read-only until returned to DRAFT.</Text>
        </Group>
        <Button variant="default" onClick={onBack}>Close</Button>
        <Button data-tour="pdf-export" onClick={exportPdf} loading={busy} disabled={!lastBlobRef.current || rendering}>Export PDF</Button>
      </Group>
    </Stack>
  );
}
